import { AuthTokenAdapter } from '../adapters/auth-token-adapter';
import { HttpInterceptorAdapter, InterceptorRequest } from '../adapters/http-interceptor-adapter';

/**
 * SharedHttpInterceptor - Environment-Agnostic Authentication Logic
 *
 * This interceptor contains the core authentication logic that works across all environments
 * (Electron main process, Angular renderer process, and web browser). It operates on
 * abstracted request/response interfaces rather than environment-specific HTTP types.
 *
 * KEY ARCHITECTURAL BENEFIT:
 * Since this interceptor is wired into the respective HTTP libraries during application
 * startup (Axios for Electron main, Angular HttpClient for renderer), it NEVER needs to
 * detect or branch based on the environment. Each environment-specific wrapper
 * (MainHttpInterceptor, RendererHttpInterceptor) handles the HTTP library integration,
 * while this shared interceptor focuses purely on authentication logic.
 *
 * FLOW:
 * 1. Environment-specific interceptor converts HTTP request to InterceptorRequest
 * 2. This shared interceptor handles authentication:
 *    - Skips auth for certain URLs (login, S3, assets, etc.)
 *    - Validates token expiration
 *    - Refreshes tokens if needed
 *    - Adds Authorization header and transforms URLs ({me} replacement)
 *    - Adds service worker bypass header
 * 3. Returns transformed request back to environment-specific interceptor
 * 4. On 401 errors, handles token refresh and retry logic
 * 5. Triggers logout on auth failures
 */
export class SharedHttpInterceptor implements HttpInterceptorAdapter {
  // Following auth0 pattern: allowedDomains (whiteListedDomains) for token attachment
  private allowedDomains = [
    'auth.uc-technologies.com',
    'api.connectuc.io',
    'staging.connectuc.engineering',
    'dev.connectuc.engineering',
    'api.instant-fax.com',
    'api-v2.instant-fax.com',
  ];

  constructor(private authTokenAdapter: AuthTokenAdapter) {}

  public async intercept(request: InterceptorRequest): Promise<InterceptorRequest> {
    const headers = { ...request.headers };
    if (request.method !== 'OPTIONS') {
      // headers['ngsw-bypass'] = '';
    }
    // Following auth0 pattern: skip if request should not be authenticated or URL is not safe for token attachment
    if (this.shouldSkipAuthenticationForUrl(request.url) || !this.isSafe(request.url)) {
      return { ...request, headers };
    }

    // Following auth0 pattern: skip if already has Authorization header
    if (headers['Authorization']) {
      // Add service worker bypass header for all API requests
      return {
        ...request,
        headers,
      };
    }

    // Following auth0 pattern: call tokenGetter for each request
    try {
      const { accessToken, jwt } = await this.authTokenAdapter.ensureValidAccessToken();
      const transformedUrl = request.url.replace('{me}', jwt.sub);
      const authHeaders = {
        ...headers,
        Authorization: `Bearer ${accessToken}`,
      };

      return {
        ...request,
        url: transformedUrl,
        headers: authHeaders,
      };
    } catch (error) {
      throw error;
    }
  }

  private shouldSkipAuthenticationForUrl(url: string): boolean {
    //do not skip login agent to queue or login-methods API
    if (url.includes('/agent/login')) {
      return false;
    }
    const skipUrls = [
      'login',
      'token',
      'refresh',
      'forgot-password',
      'reset-password',
      'cuc-media.s3',
      'reachuc-media.s3.amazonaws',
      'mmmsg-vrg.reachuc',
      'uc/branding/sso/url',
      'sso-branding',
      'assets/i18n',
      'gatherings/guest', // Guest meeting endpoints
    ];
    return skipUrls.some((skipUrl) => url.includes(skipUrl));
  }

  private isSafe(url: string): boolean {
    // Following auth0 pattern: check if URL is safe for token attachment
    const hostname = this.getHostnameFromUrl(url);

    // Check if hostname matches any allowed domain
    for (const domain of this.allowedDomains) {
      if (hostname === domain.toLowerCase()) {
        return true;
      }
    }

    // Check if it's same origin (relative URLs)
    if (this.isSameOrigin(url)) {
      return true;
    }

    return false;
  }

  private getHostnameFromUrl(url: string): string {
    try {
      if (url.startsWith('//')) {
        url = 'https:' + url;
      } else if (url.startsWith('/')) {
        // Relative URL - same origin
        return '';
      } else if (!url.includes('://')) {
        // Assume relative URL
        return '';
      }

      const parsed = new URL(url);
      return parsed.hostname.toLowerCase();
    } catch (error) {
      console.error('Error getting hostname from URL:', error);
      return '';
    }
  }

  private isSameOrigin(url: string): boolean {
    // If URL starts with / or doesn't contain ://, it's same origin
    return url.startsWith('/') || !url.includes('://');
  }
}
